import { conversationListPageURL } from './URLHelper';
import {
  hasPermissions,
  getUserPermissions,
  getCurrentAccount,
} from './permissionsHelper';

import {
  ROLES,
  CONVERSATION_PERMISSIONS,
  CONTACT_PERMISSIONS,
  REPORTS_PERMISSIONS,
  PORTAL_PERMISSIONS,
  CANNED_RESPONSE_MANAGE_PERMISSIONS,
  CANNED_RESPONSE_CREATE_PERMISSIONS,
  CANNED_RESPONSE_EDIT_PERMISSIONS,
  CANNED_RESPONSE_DELETE_PERMISSIONS,
  CAMPAIGN_PERMISSIONS,
  AUTOMATION_MANAGE_PERMISSIONS,
  AUTOMATION_VIEW_PERMISSIONS,
  AUTOMATION_CREATE_PERMISSIONS,
  AUTOMATION_EDIT_PERMISSIONS,
  AUTOMATION_DELETE_PERMISSIONS,
  FRANCHISE_VIEW_PERMISSIONS,
  FRANCHISE_MANAGE_PERMISSIONS,
} from 'dashboard/constants/permissions.js';

export const routeIsAccessibleFor = (route, userPermissions = []) => {
  const { meta: { permissions: routePermissions = [] } = {} } = route;
  return hasPermissions(routePermissions, userPermissions);
};

export const defaultRedirectPage = (to, permissions) => {
  const { accountId } = to.params;

  const permissionRoutes = [
    {
      permissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
      path: 'dashboard',
    },
    { permissions: [CONTACT_PERMISSIONS], path: 'contacts' },
    {
      permissions: [FRANCHISE_VIEW_PERMISSIONS, FRANCHISE_MANAGE_PERMISSIONS],
      path: 'franchises',
    },
    { permissions: [REPORTS_PERMISSIONS], path: 'reports/overview' },
    { permissions: [CAMPAIGN_PERMISSIONS], path: 'campaigns/mass_outbound' },
    { permissions: [PORTAL_PERMISSIONS], path: 'portals' },
    {
      permissions: [
        CANNED_RESPONSE_MANAGE_PERMISSIONS,
        CANNED_RESPONSE_CREATE_PERMISSIONS,
        CANNED_RESPONSE_EDIT_PERMISSIONS,
        CANNED_RESPONSE_DELETE_PERMISSIONS,
      ],
      path: 'automations/canned-response/list',
    },
    // ULTIMA entrada: e a pagina de pouso de quem nao casa com nenhuma
    // anterior. 'automations/macros' deixou de existir com as telas legadas;
    // o destino equivalente e a lista de Fluxos (automation_flows_list).
    // Remover a entrada sem substituto jogaria o papel personalizado de
    // automacao em 'dashboard', que ele pode nao ter permissao de ver.
    {
      permissions: [
        AUTOMATION_MANAGE_PERMISSIONS,
        AUTOMATION_VIEW_PERMISSIONS,
        AUTOMATION_CREATE_PERMISSIONS,
        AUTOMATION_EDIT_PERMISSIONS,
        AUTOMATION_DELETE_PERMISSIONS,
      ],
      path: 'automations/flows/list',
    },
  ];

  const route = permissionRoutes.find(({ permissions: routePermissions }) =>
    hasPermissions(routePermissions, permissions)
  );

  return `accounts/${accountId}/${route ? route.path : 'dashboard'}`;
};

const validateActiveAccountRoutes = (to, user) => {
  // If the current account is active, then check for the route permissions
  const accountDashboardURL = `accounts/${to.params.accountId}/dashboard`;

  // If the user is trying to access suspended route, redirect them to dashboard
  if (to.name === 'account_suspended') {
    return accountDashboardURL;
  }

  const userPermissions = getUserPermissions(user, to.params.accountId);

  const isAccessible = routeIsAccessibleFor(to, userPermissions);
  // If the route is not accessible for the user, return to dashboard screen
  return isAccessible ? null : defaultRedirectPage(to, userPermissions);
};

export const validateLoggedInRoutes = (to, user) => {
  const currentAccount = getCurrentAccount(user, Number(to.params.accountId));
  // If current account is missing, either user does not have
  // access to the account or the account is deleted, return to login screen
  if (!currentAccount) {
    return `app/login`;
  }

  const isCurrentAccountActive = currentAccount.status === 'active';

  if (isCurrentAccountActive) {
    return validateActiveAccountRoutes(to, user);
  }

  // If the current account is not active, then redirect the user to the suspended screen
  if (to.name !== 'account_suspended') {
    return `accounts/${to.params.accountId}/suspended`;
  }

  // Proceed to the route if none of the above conditions are met
  return null;
};

export const isAConversationRoute = (
  routeName,
  includeBase = false,
  includeExtended = true
) => {
  const baseRoutes = [
    'home',
    'conversation_mentions',
    'conversation_unattended',
    'inbox_dashboard',
    'label_conversations',
    'team_conversations',
    'folder_conversations',
    'conversation_participating',
  ];
  const extendedRoutes = [
    'inbox_conversation',
    'conversation_through_mentions',
    'conversation_through_unattended',
    'conversation_through_inbox',
    'conversations_through_label',
    'conversations_through_team',
    'conversations_through_folders',
    'conversation_through_participating',
  ];

  const routes = [
    ...(includeBase ? baseRoutes : []),
    ...(includeExtended ? extendedRoutes : []),
  ];

  return routes.includes(routeName);
};

export const getConversationDashboardRoute = routeName => {
  switch (routeName) {
    case 'inbox_conversation':
      return 'home';
    case 'conversation_through_mentions':
      return 'conversation_mentions';
    case 'conversation_through_unattended':
      return 'conversation_unattended';
    case 'conversations_through_label':
      return 'label_conversations';
    case 'conversations_through_team':
      return 'team_conversations';
    case 'conversations_through_folders':
      return 'folder_conversations';
    case 'conversation_through_participating':
      return 'conversation_participating';
    case 'conversation_through_inbox':
      return 'inbox_dashboard';
    default:
      return null;
  }
};

// Tipo de lista (`conversationListPageURL`) de cada rota de lista que nao tem
// parametro proprio na URL.
const LIST_ROUTE_CONVERSATION_TYPE = {
  conversation_mentions: 'mention',
  conversation_unattended: 'unattended',
  conversation_participating: 'participating',
};

// URL da lista de origem de uma conversa aberta, para o botao de voltar.
// Parte da rota de detalhe (`conversation_through_*`) e nao da de lista.
export const getConversationBackURL = ({ name, params = {} }) => {
  const listRoute = getConversationDashboardRoute(name);
  return conversationListPageURL({
    accountId: params.accountId,
    inboxId: params.inbox_id,
    label: params.label,
    teamId: params.teamId,
    customViewId: listRoute === 'folder_conversations' ? params.id : undefined,
    conversationType: LIST_ROUTE_CONVERSATION_TYPE[listRoute] || '',
  });
};

export const isAInboxViewRoute = (routeName, includeBase = false) => {
  const baseRoutes = ['inbox_view'];
  const extendedRoutes = ['inbox_view_conversation'];
  const routeNames = includeBase
    ? [...baseRoutes, ...extendedRoutes]
    : extendedRoutes;
  return routeNames.includes(routeName);
};

export const isNotificationRoute = routeName =>
  routeName === 'notifications_index';
